// Org-tier extensions list. One row per sip_account. Reuses the
// subscriber-import modal for the "Import from PBX host" flow (an
// extension import produces subscriber rows as a side effect — same
// backend path). Manual "New Extension" attaches a fresh sip_account
// to an existing subscriber; note that this only writes cloud D1
// today — real FreePBX provisioning will come once the agent grows
// a mutation endpoint for extension create.

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { fmtDateTime } from '../lib/time.js';
import { useAuth } from '../hooks/useAuth.js';
import { canOperate, canAdmin } from '../lib/roles.js';
import LoadingSpinner from '../components/shared/LoadingSpinner.jsx';
import StatusBadge from '../components/shared/StatusBadge.jsx';
import ImportMenu from '../components/shared/ImportMenu.jsx';
import ListToolbar from '../components/shared/ListToolbar.jsx';
import SortableHeader, { useSort } from '../components/shared/SortableHeader.jsx';
import VoicemailBadge from '../components/shared/VoicemailBadge.jsx';
import { useVoicemailStatus } from '../hooks/useVoicemailStatus.js';
import ActionMenu from '../components/shared/ActionMenu.jsx';
import ConfirmDeleteDialog from '../components/shared/ConfirmDeleteDialog.jsx';
import Pagination, { usePagination } from '../components/shared/Pagination.jsx';
import EditExtensionModal from '../components/shared/EditExtensionModal.jsx';

const EXT_FILTERS = [
  { key: 'all',     label: 'All' },
  { key: 'online',  label: 'Online' },
  { key: 'offline', label: 'Offline' },
  { key: 'unknown', label: 'Unknown' },
];
import ImportSubscribersModal from './ImportSubscribersModal.jsx';

// Derive an extension's real status from its live SIP registration entry
// (from /sip-registrations). registered true → online, false → offline,
// null/missing → unknown (PBX unreachable or not yet loaded).
function regStatusFor(reg) {
  if (!reg || reg.registered == null) return 'unknown';
  return reg.registered ? 'online' : 'offline';
}

function statusVariant(status) {
  switch (status) {
    case 'online':  return 'active';
    case 'offline': return 'critical';
    default:        return 'unknown';
  }
}

export default function ExtensionsList() {
  const { role } = useAuth();
  const canImport = canOperate(role);
  const canWrite  = canAdmin(role);
  const queryClient = useQueryClient();
  const sort = useSort({ col: 'username', dir: 'asc' });

  // importOpen = { source, agentId? } | null
  const [importOpen, setImportOpen] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [secretResult, setSecretResult] = useState(null);
  const [secretProgress, setSecretProgress] = useState(null);
  // Row kebab actions.
  const [editingExt, setEditingExt] = useState(null);
  const [removingExt, setRemovingExt] = useState(null);
  const navigate = useNavigate();
  const updateExt = useMutation({
    mutationFn: ({ acctId, body }) => api.patch(`/sip-accounts/${acctId}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sip-accounts'] });
      setEditingExt(null);
    },
  });
  const removeExt = useMutation({
    mutationFn: (acctId) => api.delete(`/sip-accounts/${acctId}`, { confirm: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sip-accounts'] });
      setRemovingExt(null);
    },
  });
  // Reconcile Verso's record to the PBX's, a slice at a time.
  //
  // Every reveal is a round trip through a tunnel to a PHP API, so the whole org in
  // one request timed out at 131 extensions. The endpoint now does a bounded batch
  // and hands back a cursor; this loops until it says done and shows the running
  // total, so a long job looks like progress instead of a hang.
  const importSecrets = useMutation({
    mutationFn: async () => {
      const totals = {
        eligible: 0, scanned: 0, imported: 0, unchanged: 0, missing: 0,
        failed: [], hosts: [], too_long_for_calix: [], calix_max_secret_len: 12,
      };
      let after = 0;
      // A guard, not a limit: at 20 per call this covers 2,000 extensions, and it
      // means a bug in `done` can't spin forever against someone's PBX.
      for (let call = 0; call < 100; call += 1) {
        const res = await api.post('/sip-accounts/import-secrets', { after, limit: 20 });
        totals.eligible = res.eligible ?? totals.eligible;
        totals.scanned += res.scanned || 0;
        totals.imported += res.imported || 0;
        totals.unchanged += res.unchanged || 0;
        totals.missing += res.missing || 0;
        totals.failed.push(...(res.failed || []));
        totals.too_long_for_calix.push(...(res.too_long_for_calix || []));
        for (const h of res.hosts || []) {
          if (!totals.hosts.some((x) => x.agentId === h.agentId)) totals.hosts.push(h);
        }
        totals.calix_max_secret_len = res.calix_max_secret_len ?? totals.calix_max_secret_len;
        setSecretProgress({ done: totals.scanned, of: res.eligible ?? null });
        if (res.done) break;
        after = res.next_after;
      }
      return totals;
    },
    onSuccess: (res) => {
      setSecretProgress(null);
      setSecretResult(res);
      queryClient.invalidateQueries({ queryKey: ['sip-accounts'] });
    },
    onError: (err) => {
      setSecretProgress(null);
      // Partial work is kept — each slice committed on its own — so say that
      // rather than implying the whole run was lost.
      setSecretResult({ error: err?.body?.error || err?.message || 'Could not read from the PBX' });
    },
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['sip-accounts', sort.col, sort.dir],
    queryFn:  () => api.get('/sip-accounts'),
  });

  // Live SIP registration map, keyed by sip_account id. Cheap cached call
  // (see routes/sipRegistrations.js). Refreshes on a slow interval so the
  // column stays roughly live without hammering the PBX.
  const { data: regData } = useQuery({
    queryKey: ['sip-registrations'],
    queryFn:  () => api.get('/sip-registrations'),
    refetchInterval: 60_000,
    staleTime: 45_000,
  });
  const registrations = regData?.registrations || {};

  // No PBX-host column or filter here on purpose: the DID owns the host, and an
  // extension only inherits one. Showing it on the extension invited people to
  // treat it as a property they could set, which is exactly the confusion the
  // host-ownership rule exists to remove. The host lives on the DID page.

  // Agents drive the "Import from PBX host" submenu.
  // Whether each extension has a mailbox on the PBX. Live: FreePBX owns it, and a
  // stored copy would drift the moment someone edited it there.
  const { entryFor: vmFor, hostErrors: vmHostErrors } = useVoicemailStatus();

  const { data: agentsData } = useQuery({
    queryKey: ['agents-list'],
    queryFn:  () => api.get('/agents'),
    enabled:  canImport,
  });
  const agentList = Array.isArray(agentsData) ? agentsData : [];

  // Subscribers drive the New Extension modal's subscriber picker.
  const { data: subsData } = useQuery({
    queryKey: ['subscribers-picker'],
    queryFn:  () => api.get('/subscribers'),
    enabled:  createOpen,
  });
  const subscriberList = Array.isArray(subsData)
    ? subsData
    : Array.isArray(subsData?.rows) ? subsData.rows : [];

  const allRows = useMemo(() => {
    if (Array.isArray(data))       return data;
    if (Array.isArray(data?.rows)) return data.rows;
    return [];
  }, [data]);

  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  // Decorate each row with its live registration-derived status
  // (online/offline/unknown) so filtering, counts, and sort all key off the
  // real state rather than the stored config status.
  const decorated = useMemo(
    () => allRows.map((r) => ({ ...r, regStatus: regStatusFor(registrations[r.id]) })),
    [allRows, registrations],
  );

  const filterCounts = useMemo(() => ({
    all:     decorated.length,
    online:  decorated.filter((r) => r.regStatus === 'online').length,
    offline: decorated.filter((r) => r.regStatus === 'offline').length,
    unknown: decorated.filter((r) => r.regStatus === 'unknown').length,
  }), [decorated]);
  const rows = useMemo(() => {
    let out = decorated;
    if (statusFilter !== 'all') out = out.filter((r) => r.regStatus === statusFilter);
    const s = search.trim().toLowerCase();
    if (s) {
      out = out.filter((r) => {
        const ext  = String(r.username || '').toLowerCase();
        const sub  = String(r.subscriberName || r.subscriber_name || '').toLowerCase();
        return ext.includes(s) || sub.includes(s);
      });
    }
    return out;
  }, [decorated, statusFilter, search]);

  // Client-side sort — /sip-accounts doesn't take a sort param yet.
  const sortedRows = [...rows].sort((a, b) => {
    const col = sort.col;
    const av = a[col] ?? a[camel(col)] ?? '';
    const bv = b[col] ?? b[camel(col)] ?? '';
    const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
    return sort.dir === 'desc' ? -cmp : cmp;
  });
  const { paged: pagedRows, pagination } = usePagination(sortedRows);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Extensions</h1>
        <div className="flex items-center gap-3">
          {allRows.length > 0 && (
            <span className="text-sm text-gray-500">
              {rows.length === allRows.length
                ? `${allRows.length} extension${allRows.length === 1 ? '' : 's'}`
                : `${rows.length} of ${allRows.length}`}
            </span>
          )}
          {canImport && (
            <ImportMenu
              label="Import extensions"
              items={[
                ...agentList.map((a) => ({
                  label: `From PBX host: ${a.name || a.agent_id}`,
                  onClick: () => setImportOpen({ source: 'agent', agentId: a.agent_id || a.id }),
                })),
                agentList.length === 0 && {
                  label: 'No PBX hosts registered',
                  disabled: true,
                  onClick: () => {},
                },
              ].filter(Boolean)}
            />
          )}
          {/* FreePBX owns the SIP password; Verso's copy was a placeholder for
              every imported extension. That's invisible until it isn't: a Calix
              router's built-in ATA is given its password by hand and takes no more
              than 12 characters, so the two have to be compared against something
              real. Reads only — nothing is written to any PBX. */}
          {canWrite && (
            <button
              onClick={() => importSecrets.mutate()}
              disabled={importSecrets.isPending}
              title="Read each extension's SIP password from its PBX and record it in Verso. Nothing is written to the PBX."
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {importSecrets.isPending
                ? (secretProgress?.of
                    ? `Reading from PBX… ${secretProgress.done}/${secretProgress.of}`
                    : 'Reading from PBX…')
                : 'Import SIP passwords'}
            </button>
          )}
          {canWrite && (
            <button
              onClick={() => setCreateOpen(true)}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
            >
              New Extension
            </button>
          )}
        </div>
      </div>

      {toast && (
        <div className={`rounded-md border px-4 py-2 text-sm ${
          toast.ok ? 'border-green-200 bg-green-50 text-green-700'
                   : 'border-red-200 bg-red-50 text-red-700'
        }`}>
          {toast.message}
        </div>
      )}

      {secretResult && (
        <div className={`rounded-md border px-4 py-2 text-sm ${
          secretResult.error ? 'border-red-200 bg-red-50 text-red-800' : 'border-blue-200 bg-blue-50 text-blue-900'
        }`}>
          {secretResult.error ? secretResult.error : (
            <>
              Read {secretResult.scanned} extension{secretResult.scanned === 1 ? '' : 's'} from their PBX —
              {' '}{secretResult.imported} recorded, {secretResult.unchanged} already matched
              {secretResult.missing ? `, ${secretResult.missing} with no password on the PBX` : ''}.
              {secretResult.failed?.length > 0 && (
                <div className="mt-1 text-amber-900">
                  Couldn&rsquo;t read {secretResult.failed.length}:{' '}
                  <span className="font-mono">{secretResult.failed.slice(0, 6).map((f) => f.username).join(', ')}</span>
                  {secretResult.failed.length > 6 ? ` and ${secretResult.failed.length - 6} more` : ''}.
                </div>
              )}
              {/* The reported failure, named: the PBX password can't be loaded
                  onto the router, so the line can't register until one of them
                  changes. */}
              {secretResult.too_long_for_calix?.length > 0 && (
                <div className="mt-1 font-medium text-red-800">
                  {secretResult.too_long_for_calix.length} Calix line
                  {secretResult.too_long_for_calix.length === 1 ? '' : 's'} hold a password longer than the
                  {' '}{secretResult.calix_max_secret_len}-character limit their built-in ATA accepts
                  {' '}(<span className="font-mono">
                    {secretResult.too_long_for_calix.map((c) => c.username).join(', ')}
                  </span>). Those phones can&rsquo;t register on the password the PBX has — shorten it on the
                  PBX, then set the same value on the router.
                </div>
              )}
            </>
          )}
          <button onClick={() => setSecretResult(null)} className="ml-2 underline hover:no-underline">Dismiss</button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <ListToolbar
          className="flex-1"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Filter by extension or subscriber…"
          filters={EXT_FILTERS}
          filterCounts={filterCounts}
          activeFilter={statusFilter}
          onFilterChange={setStatusFilter}
        />
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error.message}
        </div>
      )}

      {/* A host we couldn't reach makes every one of its extensions read
          "unknown". Say which host, once, rather than leaving a column of
          question marks to be interpreted. */}
      {vmHostErrors && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900">
          Couldn&rsquo;t read voicemail state from{' '}
          {Object.keys(vmHostErrors).length} PBX host{Object.keys(vmHostErrors).length === 1 ? '' : 's'} just now — those
          extensions show as unknown rather than off.
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white">
        {isLoading ? (
          <LoadingSpinner message="Loading extensions..." />
        ) : sortedRows.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-sm text-gray-500">No extensions yet.</p>
            <p className="mt-1 text-xs text-gray-400">
              Import from a PBX host or create one manually to get started.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <SortableHeader col="regStatus"     sort={sort} label="Status" />
                <SortableHeader col="username"      sort={sort} label="Extension" />
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Voicemail</th>
                <SortableHeader col="subscriberId"  sort={sort} label="Subscriber" />
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Synced</th>
                {canWrite && <th className="px-2 py-2" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pagedRows.map((r) => {
                const subId  = r.subscriberId ?? r.subscriber_id;
                const subName = r.subscriberName || r.subscriber_name;
                const reg     = registrations[r.id];
                return (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <StatusBadge status={statusVariant(r.regStatus)} label={r.regStatus} title={reg?.state || ''} />
                    </td>
                    <td className="px-4 py-2 font-mono">
                      <Link to={`/extensions/${r.id}`} className="text-blue-600 hover:underline">
                        {r.username || `#${r.id}`}
                      </Link>
                    </td>
                    <td className="px-4 py-2">
                      <VoicemailBadge entry={vmFor(r.id)} />
                    </td>
                    <td className="px-4 py-2">
                      {subId ? (
                        <Link to={`/subscribers/${subId}`} className="text-blue-600 hover:underline">
                          {subName || `#${subId}`}
                        </Link>
                      ) : (
                        <span className="text-gray-400">unassigned</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-500">
                      {r.syncedAt || r.synced_at
                        ? fmtDateTime(r.syncedAt || r.synced_at)
                        : <span className="text-gray-300">—</span>}
                    </td>
                    {canWrite && (
                      <td className="px-2 py-2 text-right">
                        <ActionMenu
                          title={`Actions for ${r.username || r.id}`}
                          items={[
                            { label: 'View details', onClick: () => navigate(`/extensions/${r.id}`) },
                            { label: 'Edit', onClick: () => { updateExt.reset(); setEditingExt(r); } },
                            { divider: true },
                            { label: 'Remove', danger: true, onClick: () => { removeExt.reset(); setRemovingExt(r); } },
                          ]}
                        />
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <Pagination {...pagination} />
      </div>

      {editingExt && (
        <EditExtensionModal
          account={editingExt}
          isPending={updateExt.isPending}
          error={updateExt.isError ? (updateExt.error?.body?.error || updateExt.error?.message) : null}
          onCancel={() => setEditingExt(null)}
          onSave={(body) => {
            if (Object.keys(body).length === 0) { setEditingExt(null); return; }
            updateExt.mutate({ acctId: editingExt.id, body });
          }}
        />
      )}
      {removingExt && (
        <ConfirmDeleteDialog
          destructive={false}
          title="Remove extension"
          body={`Remove extension ${removingExt.username || `#${removingExt.id}`}${removingExt.subscriberName ? ` (${removingExt.subscriberName})` : ''}? This soft-deletes the SIP account in Verso; PBX and GDMS teardown is handled separately.`}
          confirmLabel="Remove"
          isPending={removeExt.isPending}
          error={removeExt.isError ? (removeExt.error?.body?.error || removeExt.error?.message) : null}
          onCancel={() => setRemovingExt(null)}
          onConfirm={() => removeExt.mutate(removingExt.id)}
        />
      )}

      {importOpen && (
        <ImportSubscribersModal
          source={importOpen.source}
          agentId={importOpen.agentId}
          onClose={() => {
            setImportOpen(null);
            queryClient.invalidateQueries({ queryKey: ['sip-accounts'] });
          }}
        />
      )}

      {createOpen && (
        <NewExtensionModal
          subscribers={subscriberList}
          onClose={() => setCreateOpen(false)}
          onSuccess={(msg) => {
            setToast({ ok: true, message: msg });
            setTimeout(() => setToast(null), 4000);
            queryClient.invalidateQueries({ queryKey: ['sip-accounts'] });
          }}
        />
      )}
    </div>
  );
}

function camel(s) { return s.replace(/_([a-z])/g, (_, c) => c.toUpperCase()); }

// Exported so SubscriberDetail can offer "Add extension" with the subscriber
// preselected (pass a single-entry `subscribers` array).
export function NewExtensionModal({ subscribers, onClose, onSuccess }) {
  const [subscriberId, setSubscriberId] = useState(
    subscribers.length === 1 ? String(subscribers[0].id) : ''
  );
  const [username, setUsername] = useState('');
  const [secret, setSecret]     = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [error, setError] = useState(null);

  const mutation = useMutation({
    mutationFn: (body) => api.post(`/subscribers/${body.subscriberId}/sip-accounts`, {
      username: body.username,
      // Omitted unless the operator typed one. FreePBX generates the real
      // secret at provisioning and the agent reveals it back, so a value
      // invented here would be overwritten.
      ...(body.secret ? { secret: body.secret } : {}),
    }),
    onSuccess: () => {
      onSuccess?.(`Extension ${username} added.`);
      onClose?.();
    },
    onError: (err) => setError(err?.body?.error || err?.message || 'Create failed'),
  });

  // Two questions, both of which the operator can actually answer: whose
  // extension is it, and what number should it be. The PBX is not one of them —
  // the number routed to the extension decides that (docs/ARCHITECTURE.md § the
  // DID owns the host), and until a number is routed there is no host to record.
  const canSubmit = subscriberId && username.trim() && !mutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h3 className="text-lg font-bold text-gray-900">New Extension</h3>
        <p className="mt-1 text-xs text-gray-500">
          Adds an extension under an existing subscriber. Which PBX it lives on is
          decided by the number you route to it — until then it exists in Verso only.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!canSubmit) return;
            setError(null);
            mutation.mutate({
              subscriberId,
              username: username.trim(),
              secret: secret.trim(),
            });
          }}
          className="mt-4 space-y-3"
        >
          <div>
            <label htmlFor="new-ext-subscriber" className="block text-sm font-medium text-gray-700">Subscriber</label>
            <select
              id="new-ext-subscriber"
              value={subscriberId}
              onChange={(e) => setSubscriberId(e.target.value)}
              required
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Select a subscriber…</option>
              {subscribers.map((s) => (
                <option key={s.id} value={s.id}>{s.name || `#${s.id}`}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="new-ext-username" className="block text-sm font-medium text-gray-700">Extension number</label>
            <input
              id="new-ext-username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              placeholder="e.g. 1050"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            {!showSecret ? (
              <button
                type="button"
                onClick={() => setShowSecret(true)}
                className="text-xs font-medium text-blue-600 hover:underline"
              >
                Set the SIP secret manually
              </button>
            ) : (
              <>
                <label htmlFor="new-ext-secret" className="block text-sm font-medium text-gray-700">
                  SIP secret <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <input
                  id="new-ext-secret"
                  type="text"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  placeholder="leave blank unless matching an existing PBX extension"
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </>
            )}
            <p className="mt-1 text-xs text-gray-400">
              Leave this alone unless you&rsquo;re matching an extension that already exists on the PBX.
              The phone system sets the password when the extension is provisioned; Verso reads it back and
              pushes it to the phone. Stored encrypted with the org key.
            </p>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={mutation.isPending}
              className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {mutation.isPending ? 'Creating…' : 'Create extension'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
